import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Crown, Coins, Clock, Flame, Zap, ChevronUp, ChevronDown } from "lucide-react";
import { timeAgo, resolveAsset } from "../lib/api";

const iconMap = { crown: Crown, coin: Coins, clock: Clock, flame: Flame, zap: Zap, bolt: Zap };
const STORAGE_KEY = "spicy_feed_minimized";

export default function LiveFeed({ items, onItemClick }) {
  const [minimized, setMinimized] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, minimized ? "1" : "0"); } catch { /* ignore */ }
  }, [minimized]);

  return (
    <motion.div
      layout
      transition={{ layout: { type: "spring", stiffness: 260, damping: 28 } }}
      className="glass-red-glow rounded-2xl p-5 md:p-6 relative overflow-hidden"
      data-testid="live-feed"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <span className="live-dot" />
          <h3 className="font-display font-bold text-lg tracking-wide">
            LIVE FEED
          </h3>
          {minimized && items.length > 0 && (
            <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-red-300 bg-red-500/10 border border-red-500/30 rounded-full px-2 py-0.5">
              {items.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {!minimized && (
            <span className="text-xs text-zinc-400 font-mono uppercase tracking-widest hidden sm:block">
              Real-time
            </span>
          )}
          <button
            onClick={() => setMinimized((v) => !v)}
            data-testid="feed-minimize-toggle"
            aria-label={minimized ? "Expand live feed" : "Minimize live feed"}
            aria-expanded={!minimized}
            className="h-7 w-7 rounded-full glass-dark hover:text-red-400 hover:border-red-500/40 border border-white/10 flex items-center justify-center transition"
            title={minimized ? "Expand" : "Minimize"}
          >
            {minimized ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {!minimized && (
          <motion.div
            key="feed-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="space-y-3 max-h-[560px] overflow-y-auto pr-1 custom-scroll">
              <AnimatePresence initial={false}>
                {items.map((item) => {
                  const Icon = iconMap[item.icon] || Flame;
                  return (
                    <motion.button
                      key={item.id}
                      layout
                      initial={{ x: 60, opacity: 0, scale: 0.96 }}
                      animate={{ x: 0, opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.96 }}
                      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                      onClick={() => onItemClick && onItemClick(item)}
                      data-testid={`feed-item-${item.id}`}
                      className="w-full text-left glass-dark rounded-xl p-4 hover:border-red-500/40 hover:shadow-[0_0_22px_rgba(230,0,0,0.22)] transition-all group"
                    >
                      <div className="flex items-start gap-3">
                        <div className="h-9 w-9 rounded-full flex-shrink-0 bg-gradient-to-b from-red-500/80 to-red-950/80 border border-red-400/40 flex items-center justify-center shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_0_14px_rgba(230,0,0,0.5)]">
                          <Icon size={16} className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-white leading-snug">
                            {item.title}
                          </div>
                          {(item.subtitle || item.round_name) && (
                            <div className="text-xs text-zinc-400 mt-0.5">
                              {item.subtitle || item.round_name}
                            </div>
                          )}
                          {(item.team_a_name || item.team_b_name) && (
                            <div className="flex items-center gap-2 mt-2 text-[11px] text-zinc-500">
                              {item.team_a_logo ? (
                                <img
                                  src={resolveAsset(item.team_a_logo)}
                                  alt=""
                                  className="h-4 w-4 rounded-full object-cover"
                                />
                              ) : null}
                              {item.team_a_name && <span className="truncate">{item.team_a_name}</span>}
                              {item.team_b_name && <span className="text-red-500">vs</span>}
                              {item.team_b_logo ? (
                                <img
                                  src={resolveAsset(item.team_b_logo)}
                                  alt=""
                                  className="h-4 w-4 rounded-full object-cover"
                                />
                              ) : null}
                              {item.team_b_name && <span className="truncate">{item.team_b_name}</span>}
                            </div>
                          )}
                        </div>
                        <span className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 whitespace-nowrap">
                          {timeAgo(item.created_at)}
                        </span>
                      </div>
                    </motion.button>
                  );
                })}
              </AnimatePresence>
              {items.length === 0 && (
                <div className="text-center text-zinc-500 py-12 text-sm">
                  No updates yet. Stay tuned.
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
